import { LucideIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';

interface StatCardProps {
  title: string;
  value: number;
  change?: string;
  icon: LucideIcon;
  color: string;
  formattedValue: string;
  className?: string;
}

export function StatCard({ title, change, icon: Icon, color, formattedValue, className }: StatCardProps) {
  const isPositive = change?.startsWith('+') ?? false;
  
  return (
    <motion.div
      whileHover={{ y: -4, transition: { duration: 0.2 } }}
      className={cn(
        "group relative h-full overflow-hidden rounded-xl border border-gray-200 bg-white p-6 shadow-sm transition-all duration-300 hover:shadow-lg dark:border-gray-800 dark:bg-gray-900",
        className,
      )}
    >
      {/* Background gradient */}
      <div className={cn(
        "absolute inset-0 opacity-0 group-hover:opacity-10 transition-opacity duration-300",
        color
      )} />
      
      <div className="relative">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
              {title}
            </p>
            <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">
              {formattedValue}
            </p>
          </div>
          <div className={cn(
            "p-3 rounded-xl",
            color,
            "bg-opacity-10 group-hover:bg-opacity-20 transition-all duration-300"
          )}>
            <Icon className={cn("w-6 h-6", color.replace('bg-', 'text-'))} />
          </div>
        </div>
        
        {change ? (
          <div className="mt-4 flex items-center">
            <span className={cn(
              "inline-flex items-center text-sm font-medium",
              isPositive ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400"
            )}>
              {isPositive ? (
                <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M14.707 10.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 12.586V5a1 1 0 012 0v7.586l2.293-2.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              )}
              {change}
            </span>
            <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">
              vs. periodo anterior
            </span>
          </div>
        ) : null}
      </div>
    </motion.div>
  );
}
